<script setup lang="ts">
/*
 * Top bar — markup mirrors the V6 prototype's `<header class="top-nav">`
 * block (lines ~9252–9294 of `redesign/optimal-hr-prototype-v6.html`).
 * Layout (left → right):
 *
 *   Logo · OrgChip · NavPills (flex-1) · ResumePayrollPill ·
 *   Search · Notifications · UserMenu
 *
 * The proto's `<button class="org-chip">` and user pill are flat
 * triggers in the static HTML; in the live app the org-chip opens a
 * company-switcher dropdown and the user pill opens a user menu. Those
 * dropdown surfaces are owned by `OrgChip.vue` and `UserMenu.vue` —
 * here we just compose the slots together.
 *
 * Styling comes from `./shell-styles.css` (proto-verbatim). Tailwind
 * utilities handle layout only (max-width, padding, gaps); visual
 * chrome (.top-nav, .nav-pill, .org-chip, .icon-btn) is class-based.
 */
import { useI18n } from 'vue-i18n';
import { Bell, Layers, Search } from 'lucide-vue-next';

import AppLogo from './AppLogo.vue';
import OrgChip from './OrgChip.vue';
import NavPills from './NavPills.vue';
import ResumePayrollPill from './ResumePayrollPill.vue';
import UserMenu from './UserMenu.vue';
import { useScope } from '@/composables/useScope';

defineProps<{
  user: { name: string; email: string };
}>();

defineEmits<{
  search: [];
  openNotifications: [];
  signOut: [];
  changePassword: [];
  viewProfile: [];
}>();

const { t } = useI18n();

/*
 * Scope breadcrumb strip (proto-v7 Slice 4 — scoped DOWN).
 *
 * A thin cosmetic row under the top nav mirroring the CURRENT scope selection
 * (proto `updateScopeUI` / `#scopeBreadcrumb`, ~L15091). It derives from the
 * EXISTING `auth.activeScope` + the resolved employer list via `useScope()` —
 * the very same derivation the page-header `ScopeContextBanner` reads — so the
 * TopBar breadcrumb and the page banner stay consistent by construction (one
 * source, two render sites). It updates automatically when the user switches
 * company via the existing `OrgChip` (which mutates `auth.activeScope`). This
 * strip adds NO second switcher and does NOT change the scope model: it shows
 * a Layers icon + the `Tenant ▸ <leaf>` ownership path with the active leaf
 * bold. Under all-scopes oversight the path collapses to a single `All Scopes`
 * crumb (no specific company is pinned). `bannerProps.path` already nests an
 * `OS_PROJECT` under its owning client/vendor, matching the banner.
 */
const { bannerProps } = useScope();
</script>

<template>
  <header class="top-nav">
    <div class="app-container flex h-[64px] items-center gap-2">
      <AppLogo />
      <OrgChip />
      <NavPills />
      <div class="flex shrink-0 items-center gap-1.5">
        <ResumePayrollPill />
        <button
          type="button"
          class="icon-btn"
          :title="t('v2.shell.search.label')"
          :aria-label="t('v2.shell.search.label')"
          data-testid="topbar-search"
          @click="$emit('search')"
        >
          <Search class="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="icon-btn relative"
          :title="t('v2.shell.notifications.label')"
          :aria-label="t('v2.shell.notifications.label')"
          data-testid="topbar-notifications"
          @click="$emit('openNotifications')"
        >
          <Bell class="h-4 w-4" aria-hidden="true" />
        </button>
        <UserMenu
          :user="user"
          @sign-out="$emit('signOut')"
          @change-password="$emit('changePassword')"
          @view-profile="$emit('viewProfile')"
        />
      </div>
    </div>
    <nav
      class="scope-breadcrumb"
      data-testid="scope-breadcrumb"
      :aria-label="t('v2.scope.breadcrumb-label')"
    >
      <Layers class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span class="flex items-center gap-1.5">
        <template v-for="(seg, idx) in bannerProps.path" :key="idx">
          <span v-if="idx > 0" class="opacity-40" aria-hidden="true">▸</span>
          <b
            v-if="seg.strong ?? idx === bannerProps.path.length - 1"
            class="font-semibold text-slate-700"
            >{{ seg.label }}</b
          >
          <span v-else>{{ seg.label }}</span>
        </template>
      </span>
    </nav>
    <slot name="subnav" />
  </header>
</template>
